"use client"

import styled from "@emotion/styled"
import { CarsSwiper } from "@eurodrive/ui"
import Container from "@eurodrive/ui/Container"
import {
  getCarTypeFromClassificationCode,
  transformCarDataForPreviouslyViewed,
  transformLeasingCarDataForPreviouslyViewed,
} from "@eurodrive/utils"
import { ctaVehicleClicked } from "@eurodrive/utils/gtm"
import { Button, Tab, Tabs, Typography } from "@mui/material"
import { LeasingCarDto, SaleCarDto, SearchMetadataResponse } from "api"
import Link from "next/link"
import { useState } from "react"

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 64px 0;
  align-items: center;
  gap: 26px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 40px;
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    max-width: 249px;
  }
`

const CarsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 40px;
  }
`
const StyledTabs = styled(Tabs)`
  align-self: center;
`

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  )
}

export default function PopularCars({
  cars,
  title,
  buttonText,
  metadata,
  brandIcon,
  manufacturerCode,
}: {
  cars: (SaleCarDto | LeasingCarDto)[]
  title: string
  buttonText: string
  metadata: SearchMetadataResponse
  brandIcon: string
  manufacturerCode: number
}) {
  // Generate tabs from tags only
  const tabs = (metadata.tags || [])
    .filter(
      (tag, index, array) =>
        array.findIndex(
          (t) => t.vehicleTagForWebsiteCode === tag.vehicleTagForWebsiteCode,
        ) === index,
    )
    .map((tag) => ({
      label: tag.vehicleTagForWebsiteDescription,
      value: tag.vehicleTagForWebsiteCode,
    }))
    .filter((tab) =>
      cars.some((car) =>
        car.tags?.some((t) => t.vehicleTagForWebsiteCode === tab.value),
      ),
    )

  const [value, setValue] = useState(tabs[0]?.value)

  if (tabs.length === 0) return null

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  const filteredCars = cars
    .filter((car) =>
      car.tags?.some((t) => t.vehicleTagForWebsiteCode === value),
    )
    .slice(0, 4)

  const transformedCars = filteredCars.map((car) => {
    if ("carClassification" in car) {
      return transformCarDataForPreviouslyViewed({
        carData: car as SaleCarDto,
        brandIcon,
      })
    }
    return transformLeasingCarDataForPreviouslyViewed({
      carData: car as LeasingCarDto,
      brandIcon,
    })
  })

  // The search page fills anything the URL omits from defaultSearchFilters — carType
  // "Premium" (classifications 2 and 3) and dealType "new". A link carrying only
  // `manufacturer` therefore drops every car outside that slice, which left the button
  // on an empty result for Zeekr (classification 1) and for Volvo and Land Rover, whose
  // inventory here is used. The slice has to be stated explicitly.
  //
  // Keep the default slice whenever the brand has a car in it and fall back to the
  // brand's most common slice only when it does not. Deriving it from the first visible
  // car instead would narrow the brands that already worked, because `cars` collapses
  // new cars by model while the search does not — Audi would drop from 6 results to 4.
  //
  // Computed over the whole section rather than the active tab, so the brand-wide
  // "all models" button does not change target as tabs are switched.
  const searchSlices = cars.flatMap((car) => {
    const classificationCode =
      car.vehicleSingleValuedClassification?.[0]
        ?.vehicleSingleValuedClassificationCode
    // A car missing either field cannot describe a slice. Letting it through would
    // fall back to the "Premium" default, which is the empty-result case above.
    if (!classificationCode || !car.masterManufacturerCode) return []
    return [
      {
        dealType: !("carClassification" in car)
          ? "leasing"
          : car.carClassification === "UsedCar"
            ? "used"
            : "new",
        carType: getCarTypeFromClassificationCode(classificationCode),
        manufacturer: String(car.masterManufacturerCode),
      },
    ]
  })

  const sliceKey = (slice: (typeof searchSlices)[number]) =>
    `${slice.dealType}|${slice.carType}|${slice.manufacturer}`
  const sliceCounts = searchSlices.reduce(
    (counts, slice) =>
      counts.set(sliceKey(slice), (counts.get(sliceKey(slice)) || 0) + 1),
    new Map<string, number>(),
  )
  const searchSlice =
    searchSlices.find(
      (slice) => slice.dealType === "new" && slice.carType === "Premium",
    ) ||
    searchSlices.reduce<(typeof searchSlices)[number] | undefined>(
      (best, slice) =>
        !best ||
        (sliceCounts.get(sliceKey(slice)) || 0) >
          (sliceCounts.get(sliceKey(best)) || 0)
          ? slice
          : best,
      undefined,
    )

  const searchHref = searchSlice
    ? `/search?dealType=${searchSlice.dealType}&carType=${searchSlice.carType}&manufacturer=${searchSlice.manufacturer}`
    : `/search?manufacturer=${manufacturerCode}`

  // Tracking id stays on the page's master code so it is one stable value per brand,
  // independent of which slice the link resolved to.
  const vehicleId = `brand-page-${manufacturerCode}-${value}`

  return (
    <Container paddingMobile>
      <StyledContainer>
        <Typography variant="h2">{title}</Typography>
        <CarsContainer>
          <StyledTabs value={value} onChange={handleChange}>
            {tabs.map((tag, i) => (
              <Tab key={i} label={tag.label} value={tag.value} />
            ))}
          </StyledTabs>
          {tabs.map((el) => (
            <CustomTabPanel key={el.value} value={value} index={el.value}>
              <CarsSwiper cars={transformedCars} />
            </CustomTabPanel>
          ))}
        </CarsContainer>
        <StyledButton
          variant="primary"
          size="large"
          LinkComponent={Link}
          href={searchHref}
          onClick={() => ctaVehicleClicked(vehicleId, "brand_page")}
        >
          {buttonText}
        </StyledButton>
      </StyledContainer>
    </Container>
  )
}
